// Sélection d'une voix de synthèse française la plus naturelle possible (moins "robotique"
// que la voix par défaut du navigateur), utilisée par les narrations du simulateur MonAlerte.

// Suivi d'un geste utilisateur réel sur la page : les navigateurs (Chrome en tête) bloquent
// silencieusement l'AudioContext ET peuvent bloquer/coincer la synthèse vocale tant qu'aucune
// interaction n'a eu lieu. On ne tente donc un déclenchement automatique qu'après un premier geste.
let userHasInteracted = false;

if (typeof window !== 'undefined') {
  const markInteracted = () => {
    userHasInteracted = true;
    window.removeEventListener('pointerdown', markInteracted, true);
    window.removeEventListener('keydown', markInteracted, true);
    window.removeEventListener('touchstart', markInteracted, true);
  };
  window.addEventListener('pointerdown', markInteracted, { capture: true, passive: true, once: true });
  window.addEventListener('keydown', markInteracted, { capture: true, passive: true, once: true });
  window.addEventListener('touchstart', markInteracted, { capture: true, passive: true, once: true });
}

export const hasUserInteracted = () => userHasInteracted;

const PREFERRED_VOICE_KEYWORDS = [
  'google français', 'google french',
  'thomas', 'amélie', 'aurélie', 'audrey', 'léa', 'lea',
  'natural', 'enhanced', 'premium', 'neural', 'siri', 'wavenet'
];

let cachedVoices = [];

const refreshVoices = () => {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return [];
  const voices = window.speechSynthesis.getVoices();
  if (voices && voices.length) cachedVoices = voices;
  return cachedVoices;
};

// Choisit la meilleure voix française disponible sur l'appareil de l'utilisateur
export const pickBestFrenchVoice = () => {
  const voices = refreshVoices();
  const frVoices = voices.filter(v => v.lang && v.lang.toLowerCase().startsWith('fr'));
  if (frVoices.length === 0) return null;

  // 1. Voix reconnue comme naturelle/premium par son nom
  const premium = frVoices.find(v =>
    PREFERRED_VOICE_KEYWORDS.some(kw => v.name.toLowerCase().includes(kw))
  );
  if (premium) return premium;

  // 2. Sinon une voix locale au système (généralement de meilleure qualité qu'une voix réseau générique)
  const localFr = frVoices.find(v => v.localService);
  if (localFr) return localFr;

  // 3. Sinon la première voix française disponible
  return frVoices[0];
};

// Lit un texte en français avec la meilleure voix disponible + un débit légèrement plus naturel
export const speakFrench = (text, { onStart, onEnd, onError, rate = 0.96, pitch = 1.02 } = {}) => {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    if (onError) onError();
    return false;
  }

  // Garde impérative : sur Chrome, un cancel() + speak() déclenché deux fois de suite (une fois par
  // l'évènement voiceschanged, une fois par le filet de sécurité setTimeout) coince silencieusement
  // le moteur de synthèse — aucune erreur, aucun son. On ne laisse donc parler qu'un seul déclencheur.
  let hasSpoken = false;
  const speakNow = () => {
    if (hasSpoken) return;
    hasSpoken = true;

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'fr-FR';
    utterance.rate = rate;
    utterance.pitch = pitch;
    const bestVoice = pickBestFrenchVoice();
    if (bestVoice) utterance.voice = bestVoice;
    utterance.onstart = () => onStart && onStart();
    utterance.onend = () => onEnd && onEnd();
    utterance.onerror = () => onError && onError();
    window.__activeUtterance = utterance;

    // Léger différé après cancel() : contournement d'un bug Chrome connu où speak() appelé dans le
    // même tick qu'un cancel() ne produit silencieusement aucun son.
    setTimeout(() => {
      window.speechSynthesis.speak(utterance);
    }, 30);
  };

  const voices = refreshVoices();
  if (voices.length === 0) {
    // Chrome charge la liste des voix de façon asynchrone au premier appel
    window.speechSynthesis.onvoiceschanged = () => speakNow();
    setTimeout(speakNow, 250); // filet de sécurité si l'évènement ne se déclenche jamais
  } else {
    speakNow();
  }

  return true;
};

export const stopSpeaking = () => {
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
};

export const pauseSpeaking = () => {
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.pause();
  }
};

export const resumeSpeaking = () => {
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.resume();
  }
};
